import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import type { ICardHudConfig } from '../../../../../helpers';
import { type ILevelItem, LevelState } from '../../../helpers';
import { ScCardHud } from './styles';

const CardHudBackground: FC<ICardHudConfig> = (config) => {
  if (config.kind === 'image') {
    return (
      <ImageComponent alt="" className="cardHudBg" fill src={config.src} />
    );
  }

  return (
    <svg
      aria-hidden="true"
      className="cardHudBg"
      fill="none"
      preserveAspectRatio="none"
      viewBox={config.viewBox}
    >
      <path
        className="cardHudFill"
        d={config.fillPath}
        fill="currentColor"
        fillOpacity="0.2"
      />
      <path
        className="cardHudStroke"
        d={config.strokePath}
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  );
};

type CardHudProps = Pick<
  ILevelItem,
  | 'state'
  | 'progress'
  | 'progressLabelKey'
  | 'reward'
  | 'rewardExtra'
  | 'rewardLabelKey'
  | 'rewardSuffixKey'
  | 'rewardSuffixOnNewLine'
> & {
  namespace: string;
  cardHud: ICardHudConfig;
};

export const CardHud: FC<CardHudProps> = ({
  state,
  progress,
  progressLabelKey,
  reward,
  rewardExtra,
  rewardLabelKey,
  rewardSuffixKey,
  rewardSuffixOnNewLine,
  cardHud,
  namespace,
}) => {
  const { t } = useTranslation(namespace);
  const rewardText = rewardLabelKey ? t(rewardLabelKey) : reward;
  const rewardSuffix =
    !rewardLabelKey && rewardSuffixKey ? t(rewardSuffixKey) : undefined;

  return (
    <ScCardHud $lvlState={state}>
      <CardHudBackground {...cardHud} />
      <span className="hudProgress">
        {progressLabelKey ? t(progressLabelKey) : progress}
      </span>
      <span className="hudReward">
        {state === LevelState.Locked && (
          <IconComponent name="lock" size="16px" />
        )}
        {rewardSuffix && rewardSuffixOnNewLine ? (
          <span className="hudRewardStack">
            <span>{rewardText}</span>
            <span>{rewardSuffix}</span>
          </span>
        ) : (
          <>
            {rewardText}
            {rewardSuffix && ` ${rewardSuffix}`}
          </>
        )}
      </span>
      {!rewardLabelKey && rewardExtra && (
        <span className="hudReward hudRewardExtra">{rewardExtra}</span>
      )}
    </ScCardHud>
  );
};
